import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { SITE } from '../data/site';
import { breadcrumbJsonLd } from '@drzach/website-toolkit';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import InteriorLayout from '../components/InteriorLayout';
import Button from '../components/ui/Button';
import CtaPanel from '../components/ui/CtaPanel';
import SectionHeading from '../components/ui/SectionHeading';
import StarRating from '../components/ui/StarRating';
import { clinicHero } from '../components/pages/clinicPhotos';

/** "Read more" reveal for the longer stories (content stays in the DOM). */
function ReadMore({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <>
      <button type="button" className="ve-page-more" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        {open ? 'Show less' : label}
        <ChevronDown aria-hidden="true" focusable="false" />
      </button>
      <div id={id} className="ve-page-more-panel" hidden={!open}>
        {children}
      </div>
    </>
  );
}

function Story({ title, name, children }: { title: string; name: string; children: ReactNode }) {
  return (
    <article className="ve-page-story">
      <StarRating />
      <h2>{title}</h2>
      {children}
      <cite>{name}</cite>
    </article>
  );
}

export default function PatientReviews() {
  return (
    <InteriorLayout
      bodyClasses={['page-patient-reviews']}
      title="Patient Reviews"
      lead="What our Royal Oak patients say"
      heroVariant="photo"
      heroImage={{ ...clinicHero('mom-holding-newborn-waiting-room', 'A mom holding her newborn in the Van Every waiting room', '50% 18%'), sizes: '(min-width: 1024px) 60vw, 100vw', focalMobile: '50% 12%' }}
      heroClassName="ve-photo-hero--split"
      heroPrimary={{ label: 'Book Online', to: SITE.booking.chooserPath }}
      heroSecondary={{ label: 'Google Reviews', to: '/reviews/' }}
      breadcrumbs={[{ name: 'Patient Reviews' }]}
    >
      <Seo
        title="Patient Reviews | Van Every Family Chiropractic Center"
        description="Read patient reviews and testimonials from Van Every Family Chiropractic Center in Royal Oak, MI. See what our patients say about their chiropractic care."
        canonical="/patient-reviews"
      />
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Home', url: `https://${SITE.domain}/` },
        { name: 'Patient Reviews', url: `https://${SITE.domain}/patient-reviews` },
      ])} />

      <div className="ve-page-flow">
        <section className="ve-page-split" aria-labelledby="stories-intro-title">
          <SectionHeading as="h2" id="stories-intro-title" eyebrow="Patient stories" title="In Their Own Words" />
          <div className="ve-page-split-body ve-prose">
            <p>
              At <Link to="/about-us/">Van Every Family Chiropractic Center</Link> we love when patients
              share with us their feedback and comments after chiropractic care. Please read our patient
              testimonials below to see what other people have achieved through their tailored treatment
              plans with us.
            </p>
            <p>
              Share your stories too! Send us your feedback so that we can help to spread the word about
              the benefits of natural chiropractic care. You can also read our recent{' '}
              <Link to="/reviews/">Google reviews and patient stories sorted by condition</Link>.
            </p>
            <div className="ve-btn-row ve-page-actions">
              <Button to="/contact-us/">Contact us</Button>
              <Button variant="outline" to="/video-testimonials/">
                Watch video testimonials
              </Button>
            </div>
          </div>
        </section>

        <div className="ve-page-stories ve-page-narrow ve-page-narrow--center">
          <Story title="Successful Twin Pregnancy!" name="Stacey H.">
            <p>
              After a college injury to my neck and lower back I started chiropractic care. It took about
              10 years of different doctors to find myself at Van Every Family Chiropractic Center and
              finally experience relief. I've been a patient now for a little over a decade and feel that
              Van Every Family Chiropractic Center has played an important role in not only my physical
              health but my mental health as well. I've been able to experience relief from pain,
              improvement with sleep and overall well-being when I'm consistent with adjustments.
            </p>
            <ReadMore label="Read more from Stacey">
              <p>
                Dr. Saylor has supported me through one of the challenging times in my life as we
                struggled with infertility and pregnancy losses for 7 years. Through the struggles came
                the joy of a healthy, successful twin pregnancy that I credit greatly to the chiropractic
                care I received during fertility treatments and throughout 8 months of pregnancy. I was
                able to remain extremely comfortable and issues that arose were resolved after an
                adjustment. My daughters were adjusted within a week of birth and continue to receive
                care almost 3 years later. The adjustments they've received resulted in the girls being
                excellent sleepers and healthy, happy toddlers.
              </p>
              <p>
                Not only has Dr. Saylor helped with physical and emotional pain but the entire Van Every
                Family Chiropractic Center staff has played a big role in our care. The whole staff is
                kind, genuine and caring… nothing I have ever experienced at any doctors office. Always
                going above and beyond!! I will forever remember the 1st day I dropped my girls at
                daycare. It was an emotional challenge so I called the office to see if I could squeeze
                in an adjustment to help. Without hesitation they were accommodating and the moment I
                walked in one of the secretaries remembered and asked how drop-off went and I burst into
                tears. Within minutes I was wrapped in a hug and felt so supported by everyone. Thank you!
                Van Every Family Chiropractic Center will continue to play an integral role in the health
                of our family.
              </p>
            </ReadMore>
          </Story>

          <Story title="I Now Can Lift My Son With Ease!" name="Linda J.">
            <p>
              My name is Linda, and I have been receiving chiropractic adjustments at Van Every Family
              Chiropractic Center. Chiropractic adjustments have relieved tension throughout my body, and
              has been keeping me balanced and focused!! I have noticed the following changes: I have
              been more relaxed, I sleep better, am less stressed and can focus so much better!! I now
              can lift my son with ease! The staff is friendly and very helpful! The Doctors are caring,
              great and very knowledgeable!! The office is clean, comfortable and welcoming! Chiropractic
              care helps in many aspects, not just physically!
            </p>
          </Story>

          <Story title="Highly Recommend" name="Kaitlyn P.">
            <p>
              We have had only the best experience at Van Every Family Chiropractic Center family
              chiropractic! I started going to Van Every Family Chiropractic Center after attending the
              baby bumps and beer bellies event that helped inform me about the benefits of chiropractic
              during pregnancy and for children. The adjustments really helped me to be more comfortable
              during my pregnancy. I just had my second daughter two weeks ago and she has already had
              three adjustments. My oldest daughter is three and has also received several adjustments.
              My husband started getting adjustments about a month ago and it has made a world of
              difference for him he is feeling better already. Dr Anna along with all of the staff at Van
              Every Family Chiropractic Center make you feel like family. I would highly recommend anyone
              to go to Van Every Family Chiropractic Center for their chiropractic care.
            </p>
          </Story>

          <Story title="We Are So Much Healthier" name="Stephanie, Chris, & Raya S.">
            <p>
              Five years ago, I was looking for something different in a chiropractor. I had been in
              traditional chiropractic practices for over ten years and I just wasn't getting any better.
              Worse, it was actually hurting me. Then I found Van Every Family Chiropractic Center
              Chiropractic. From the beginning, I could tell I was going to love it. The office staff is
              so amazing; they are warm and welcoming. They go out of their way to remember your name,
              your family, etc.
            </p>
            <ReadMore label="Read more from Stephanie and Chris">
              <p>
                The doctors are so knowledgeable about not only your back, but your overall health. The
                adjustments don't hurt; they are so gentle but effective. My husband had a pinched nerve
                and the care he received at Van Every Family Chiropractic Center allowed him to return to
                his work and life within a matter of days, when treatment at the hospital did little to
                nothing. He's been going ever since.
              </p>
              <p>
                When our daughter was born, she was adjusted from the start. She has been so healthy and
                her immune system is so strong. I attribute that to the amazing care she gets from this
                office. They are always the first call we make if any of us are feeling under the
                weather. Many times, it is all we need to do! In addition, they have amazing seminars
                about different health topics, yoga classes, and don't even get me started on their
                fantastic massage therapists!
              </p>
              <p>
                I also love how connected Dr. Saylor is to her community. She has different community
                days for children at least 2-3 times per year, and they are free! At some point we
                stopped feeling like this office was just a service, and now feel we're part of a family.
                We are so much healthier, more focused, relaxed and stronger because of the care we
                receive at Van Every Chiropractic. Get in there… you won't regret it!
              </p>
            </ReadMore>
          </Story>

          <Story title="Effortless Efficiency" name="Sohail B.">
            <p>
              Easily one of the best service experiences I've ever had. It's rare to find any business or
              medical office with such effortless efficiency while still making you feel like your care
              is always the highest priority. Everyone is very welcoming, knowledgeable, and helpful. I
              have already told multiple friends and family about the practice after only two
              appointments. Thank you to the entire staff- 10/10.
            </p>
          </Story>

          <Story title="My Symptoms Were All Gone" name="Jim E.">
            <p>
              In the summer of 2011 I was coaching my daughter's softball team and we were having
              practice, I was playing catch with my daughter and she looked at me and asked me if I was
              OK. I told her I was fine, one of the other coaches came over and looked at me and said
              that my face was drooping on one side and my eye on that side was not blinking. We drove
              home from practice and my wife took me to Urgent Care where they diagnosed me with Bells
              Palsy and gave me medication to help it. Luckily I had no pain from it except for the one
              eye that was dry because it would not blink. My family doctor confirmed the Bells Palsy
              diagnosis. I couldn't get into see a specialist for three weeks. My wife suggested that I
              go to Van Every Family Chiropractic Center Chiropractic and have Dr Saylor look at me. My
              wife and kids were patients of Dr Saylor but I was not at the time. Dr Saylor adjusted me
              and had me come back for 3 visits in the first week. My first adjustment was within 24
              hours of the onset and I suffered very little. By the time I got in to the specialist in 3
              weeks my symptoms were all gone. I've been a patient at Van Every Family Chiropractic
              Center Chiropractic ever since.
            </p>
          </Story>

          <Story title="Success Story!" name="Heather S.">
            <p>
              My daughter heard a news story on Monday that really upset her. The news was on in my room
              and loud enough that she could hear it in her bathroom. She started sobbing. From Monday to
              Friday when we came to the office to be adjusted she was clung to me. Scared and wouldn't
              go upstairs, downstairs, or sleep without me. She was adjusted Friday night, came home, ate
              dinner and went to bed. Not only did she stay upstairs but she slept in her bed alone!
              Yahoo! She seems back to normal today also! She has slept in her room since! Thank you!
            </p>
          </Story>

          <Story title="I love Dr. Saylor" name="Stacey H.">
            <p>
              As my 2.5-year-old girls leave the office today after an adjustment and say "Momma, I love
              Dr. Saylor. She so nice. She so beautiful" (repeatedly stated by one little one that has
              been experiencing "BIG" emotions and fears as of late). I know in my heart we have been
              served, cared for and I need to be more consistent with our adjustments because Van Every
              Family Chiropractic Center (The doctors and staff) makes a difference in my life and more
              importantly my daughters!
            </p>
          </Story>

          <Story title="So Thankful For Finding Van Every Family Chiropractic Center!" name="Heather S.">
            <p>
              My daughter started with symptoms at 2 weeks old. I was nursing and she would projectile
              vomit. She had a fever at 5 weeks old. She was diagnosed with asthma. She has severe food
              allergies. She was chronically constipated. She is a seriously picky eater. She gets motion
              sick in the car. I did what the doctors told me to do…we pumped her with antibiotics and
              steroids each time she got sick…we were in a vicious cycle it was too much for her little
              body!
            </p>
            <ReadMore label="Read more from Heather">
              <p>
                By age 8 she had been on antibiotics so many times I couldn't count. She was always sick
                and it seemed like we were always going to the doctors. A friend of mine told me about
                Van Every Family Chiropractic Center (she had been trying to get me to go for a long
                time). We took the leap and saw Dr. Saylor for our intake. The questions she asked got me
                thinking and made me sick…but I knew there was a better way. We started seeing Dr. Saylor
                every week 2-3 times a week. My daughters' body had shut down, she was no longer fighting
                anything, she was always in a state of fighting and her body was never rested and ready
                to fight when she did get sick. After a few months of being adjusted we could see a
                change. She still gets sick but she fights it, and she is able to fight because her body
                is resting and working correctly. After 2 years now of being adjusted I have learned many
                natural ways to boost her immune system when needed. She gets adjusted 2 times a month
                now and she is so much healthier. She is still a picky eater, she still has asthma and
                is allergic to foods but her body is healing!
              </p>
              <p>
                My son also started coming to Van Every Family Chiropractic Center shortly after my
                daughter…since we were making the drive! I didn't have too many concerns about him at
                the beginning. Dr. Saylor and I were talking and I mentioned that he couldn't ride a bike
                yet which was odd for an 11 year-old. Dr. Saylor explained to me about his trunk and
                instability, it showed in his shoulders and torso. He stands quite tall now and could
                ride a bike by that next summer! He has had some health issues since starting and the
                doctors worked with us to find alternatives to all the medicines the pediatrician wanted
                to prescribe.
              </p>
              <p>
                I myself started coming to Van Every Family Chiropractic Center first. I had intense
                shoulder pain on the inside of my right shoulder blade. I had been through many tests,
                been to a traditional chiropractor, physical therapy, been to a muscle specialist, and
                they were about to send me back to physical therapy again. I asked if they had ever heard
                of KST chiropractic. The OMM doctor told me he had not but it would be worth a try. I
                started coming to Van Every Family Chiropractic Center instead of physical therapy. I had
                been there, done that with little help. I was having headaches constantly and those in
                turn affected my mood. I was always grouchy and mean because I hurt. It took some time
                for the muscles to calm down. I can still feel pain in that area at times but the
                intensity, severity and frequency have been greatly reduced by chiropractic care. Dr.
                Saylor can usually tell before I even say anything about how and where it hurts. She will
                move my arm, back and neck while adjusting. I know if it is flaring up it just needs an
                adjustment and I am back to feeling great!
              </p>
              <p>
                The doctors are a wealth of knowledge and so open to answering questions. They really
                listen and understand. Being adjusted is easy and comfortable! The office staff is so
                friendly and greet you with a warm welcome every time we arrive. They do their best to
                accommodate appointments and are flexible when we are stuck in traffic. I continue to
                bring my children and myself from Novi twice a month because I believe in the benefits of
                chiropractic care and I have seen first hand what it has done for myself and my children.
              </p>
              <p>
                My mom even drives over with us now because she saw first hand the changes we were
                experiencing! I am so thankful for finding Van Every Family Chiropractic Center!
              </p>
            </ReadMore>
          </Story>

          <Story title="More Than Just Pain Relief" name="Rob C.">
            <p>
              I initially started going to Van Every Family Chiropractic Center in May of 2018 for back
              pain and numbness down my left leg. However, they don't just address your symptoms, they
              care for you as a whole person. Their initial assessment was impressive and eye opening.
              Through their care I found relief in my back, elimination of the numbness in my leg and a
              release of anxiety and stress. The day the numbness went away down my leg, I suddenly felt
              an overwhelming sense of stress relief. To the point I became very emotional. I have found
              I handle anxiety and stress a lot better and feel like I'm seeing a friend instead of a
              doctor. My wife also started seeing them for allergies and of course they have helped with
              more than just that! We cannot say enough about Van Every Family Chiropractic Center!
            </p>
          </Story>

          <Story title="Made My Life So Much Better" name="Barb B.">
            <p>
              I have been going to Van Every Family Chiropractic Center since 2001. Besides my children
              and grand children, (and OK, the rest of the family), Dr. Saylor is the best person who came
              into my life. I don't know where I would be without the support of this doctor, her
              associates and staff…. They have made my life so much better in so many ways. I love you
              all!!!!!
            </p>
          </Story>
        </div>

        <CtaPanel
          variant="large"
          title="Ready to Write Your Own Story?"
          text="For more information or to schedule an appointment, contact us today."
          button={{ label: 'Contact us', to: '/contact-us/' }}
        />
      </div>
    </InteriorLayout>
  );
}
